<template>
  <div class="wool-line-preview">
    <div v-if="!data || data.length === 0" class="no-data">
      <p>No wool line data detected in the uploaded file</p>
    </div>
    
    <div v-else class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Line ID</th>
            <th>PIC</th>
            <th>Description</th>
            <th>Mulesing Status</th>
            <th>Classer ID</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(woolLine, index) in data" :key="index">
            <td>
              <input 
                type="text" 
                v-model="woolLine.LineID" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.LineID }"
              >
              <div v-if="validationErrors[index]?.LineID" class="error-message">
                {{ validationErrors[index].LineID }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="woolLine.PIC" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.PIC }"
              >
              <div v-if="validationErrors[index]?.PIC" class="error-message">
                {{ validationErrors[index].PIC }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="woolLine.Description" 
                class="table-input"
              >
            </td>
            <td>
              <select v-model="woolLine.MulesingStatus" class="table-select">
                <option value="NM">NM (Non-Mulesed)</option>
                <option value="CM">CM (Ceased Mulesing)</option>
                <option value="M">M (Mulesed)</option>
                <option value="AA">AA (Analgesic/Anaesthetic)</option>
              </select>
            </td>
            <td>
              <input 
                type="text" 
                v-model="woolLine.ClasserID" 
                class="table-input"
              >
            </td>
            <td class="actions-cell">
              <button class="btn-icon" @click="expandWoolLine(index)" :title="woolLine.expanded ? 'Collapse' : 'Expand'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline v-if="!woolLine.expanded" points="6 9 12 15 18 9"></polyline>
                  <polyline v-else points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button class="btn-icon delete" @click="removeWoolLine(index)" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Expanded Wool Line Details -->
      <div v-for="(woolLine, index) in data" :key="`details-${index}`" class="expanded-details" v-show="woolLine.expanded">
        <div class="details-grid">
          <div class="detail-group">
            <label>Bale Count</label>
            <input type="number" v-model.number="woolLine.BaleCount" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Total Weight (kg)</label>
            <input type="number" v-model.number="woolLine.TotalWeight_kg" class="detail-input">
          </div>
        </div>
      </div>
    </div>
    
    <div class="add-item-row">
      <button class="btn-add" @click="addNewWoolLine">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Wool Line
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      validationErrors: [],
      woolLineTemplate: {
        LineID: '',
        PIC: '',
        Description: '',
        MulesingStatus: 'NM',
        ClasserID: '',
        BaleCount: 0,
        TotalWeight_kg: 0,
        expanded: false
      }
    };
  },
  
  watch: {
    data: {
      handler(newData) {
        // Initialize expanded property if not present
        if (newData) {
          newData.forEach(woolLine => {
            if (woolLine.expanded === undefined) {
              this.$set(woolLine, 'expanded', false);
            }
          });
        }
        
        // Reset validation errors when data changes
        this.validationErrors = Array(newData?.length || 0).fill(null);
        
        // Emit updated data
        this.$emit('update', newData);
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    expandWoolLine(index) {
      if (this.data[index]) {
        this.$set(this.data[index], 'expanded', !this.data[index].expanded);
      }
    },
    
    removeWoolLine(index) {
      if (confirm('Are you sure you want to remove this wool line?')) {
        const updatedData = [...this.data];
        updatedData.splice(index, 1);
        this.$emit('update', updatedData);
      }
    },
    
    addNewWoolLine() {
      const updatedData = [...this.data];
      updatedData.push({...this.woolLineTemplate});
      this.$emit('update', updatedData);
    },
    
    validateWoolLines() {
      let isValid = true;
      const errors = Array(this.data.length).fill(null).map(() => ({}));
      
      this.data.forEach((woolLine, index) => {
        // Validate LineID
        if (!woolLine.LineID) {
          errors[index].LineID = 'Line ID is required';
          isValid = false;
        } else if (!/^[A-Z]{3}-\d{2}-\d{3}$/.test(woolLine.LineID)) {
          errors[index].LineID = 'Invalid Line ID format (e.g., KOO-25-001)';
          isValid = false;
        }
        
        // Validate PIC
        if (!woolLine.PIC) {
          errors[index].PIC = 'PIC is required';
          isValid = false;
        }
      });
      
      this.validationErrors = errors;
      return isValid;
    }
  }
};
</script>

<style scoped>
.wool-line-preview {
  margin-bottom: 24px;
}

.no-data {
  padding: 40px;
  text-align: center;
  background-color: #f9fafb;
  border-radius: 6px;
  color: #6b7280;
}

.data-table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f9fafb;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.table-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.table-input:focus {
  border-color: #3A895F;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 137, 95, 0.2);
}

.table-input.has-error {
  border-color: #ef4444;
}

.error-message {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.table-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.actions-cell {
  white-space: nowrap;
  width: 80px;
}

.btn-icon {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.btn-icon:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.btn-icon.delete:hover {
  background-color: #fee2e2;
  color: #b91c1c;
}

.expanded-details {
  background-color: #f9fafb;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e5e7eb;
  border-top: none;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.detail-group {
  display: flex;
  flex-direction: column;
}

.detail-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.detail-input {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.add-item-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: white;
  border: 1px dashed #d1d5db;
  color: #3A895F;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add:hover {
  background-color: #f9fafb;
  border-color: #3A895F;
}
</style>
